import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  type Category,
} from '@/api/categories'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet } from '@/components/ui/sheet'
import { CategoryIcon } from '@/lib/categoryIcon'
import { IconPicker } from '@/components/IconPicker'
import { CATEGORY_ICON_OPTIONS } from '@/lib/categoryIconOptions'
import { cn } from '@/lib/utils'

type CategoryType = 'INCOME' | 'EXPENSE'

const SECTIONS: { type: CategoryType; title: string }[] = [
  { type: 'EXPENSE', title: 'Expense' },
  { type: 'INCOME', title: 'Income' },
]

function TypeToggle({
  value,
  onChange,
  disabled,
}: {
  value: CategoryType
  onChange: (t: CategoryType) => void
  disabled?: boolean
}) {
  return (
    <div className="flex gap-1 rounded-xl bg-[var(--fl-surface-hover)] p-1">
      {(['EXPENSE', 'INCOME'] as const).map((t) => (
        <Button
          key={t}
          type="button"
          variant="ghost"
          aria-pressed={value === t}
          disabled={disabled}
          onClick={() => onChange(t)}
          className={cn(
            'h-auto flex-1 rounded-lg py-1.5 text-sm font-medium transition-colors',
            value === t
              ? 'bg-[var(--fl-surface)] text-[var(--fl-text-1)] shadow-sm'
              : 'text-[var(--fl-text-2)]'
          )}
        >
          {t === 'EXPENSE' ? 'Expense' : 'Income'}
        </Button>
      ))}
    </div>
  )
}

function FormButtons({
  submitLabel,
  pending,
  onClose,
}: {
  submitLabel: string
  pending: boolean
  onClose: () => void
}) {
  return (
    <div className="flex gap-3 pt-2">
      <Button type="button" variant="outline" onClick={onClose} className="h-11 flex-1">
        Cancel
      </Button>
      <Button type="submit" disabled={pending} className="h-11 flex-1">
        {pending ? 'Saving…' : submitLabel}
      </Button>
    </div>
  )
}

// --- Add Category ---

function AddCategorySheet({
  initialType,
  onClose,
}: {
  initialType: CategoryType
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [icon, setIcon] = useState('')
  const [type, setType] = useState<CategoryType>(initialType)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => createCategory({ name, icon, type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      onClose()
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      setError(
        status === 409
          ? 'A category with this name already exists.'
          : 'Something went wrong. Please try again.'
      )
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    if (!icon.trim()) {
      setError('Icon is required.')
      return
    }
    mutation.mutate()
  }

  return (
    <Sheet title="Add Category" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <TypeToggle value={type} onChange={setType} />
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-[var(--fl-text-2)]">Name</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            placeholder="e.g. Rent"
          />
        </label>
        <div>
          <span className="mb-1 block text-sm font-medium text-[var(--fl-text-2)]">Icon</span>
          <IconPicker value={icon} onChange={setIcon} options={CATEGORY_ICON_OPTIONS} />
        </div>
        {error && <p className="text-xs text-[var(--fl-error)]">{error}</p>}
        <FormButtons submitLabel="Add" pending={mutation.isPending} onClose={onClose} />
      </form>
    </Sheet>
  )
}

// --- Edit Category ---

function EditCategorySheet({ category, onClose }: { category: Category; onClose: () => void }) {
  const queryClient = useQueryClient()
  const locked = category.locked === true
  const [name, setName] = useState(category.name)
  const [icon, setIcon] = useState(category.icon)
  const [type, setType] = useState<CategoryType>(category.type)
  const [error, setError] = useState<string | null>(null)
  const [typeConflictCount, setTypeConflictCount] = useState<number | null>(null)

  const mutation = useMutation({
    // monthlyLimit uses send-current-or-lose-it semantics on the API — omitting it would
    // clear a monthly budget set from the Budget page, so it must always be passed through
    // (a yearly budget is kept either way). Income takes no budget, so a move to Income drops it.
    mutationFn: () =>
      updateCategory(category.id, {
        name,
        icon,
        type,
        monthlyLimit: type === 'INCOME' ? null : (category.monthlyLimit ?? null),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      onClose()
    },
    onError: (err: unknown) => {
      const resp = (err as { response?: { status?: number; data?: { transactionCount?: number } } })
        ?.response
      if (resp?.status === 409 && resp.data?.transactionCount != null) {
        setTypeConflictCount(resp.data.transactionCount)
      } else if (resp?.status === 409) {
        setError('A category with this name already exists.')
      } else {
        setError('Something went wrong. Please try again.')
      }
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setTypeConflictCount(null)
    mutation.mutate()
  }

  return (
    <Sheet title="Edit Category" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {locked && (
          <p className="flex gap-2.5 rounded-xl bg-[var(--fl-surface-subtle)] p-3 text-[13px] leading-snug text-[var(--fl-text-2)]">
            <Lock size={15} aria-hidden="true" className="mt-px shrink-0" />
            {category.name.toLowerCase() === 'rounded off'
              ? 'Settle up records extra paid that nobody owes back here'
              : 'Loans record their interest and write-offs here'}
            , so its name and type stay fixed. You can still change the icon.
          </p>
        )}
        <TypeToggle value={type} onChange={setType} disabled={locked} />
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-[var(--fl-text-2)]">Name</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            readOnly={locked}
            className={cn(locked && 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]')}
          />
        </label>
        <div>
          <span className="mb-1 block text-sm font-medium text-[var(--fl-text-2)]">Icon</span>
          <IconPicker value={icon} onChange={setIcon} options={CATEGORY_ICON_OPTIONS} />
        </div>
        {error && <p className="text-xs text-[var(--fl-error)]">{error}</p>}
        {typeConflictCount !== null && (
          <p className="text-xs text-[var(--fl-error)]">
            <strong>{typeConflictCount}</strong> transaction{typeConflictCount !== 1 ? 's' : ''} use
            this category with its current type. Reassign them before changing the type.
          </p>
        )}
        <FormButtons submitLabel="Save" pending={mutation.isPending} onClose={onClose} />
      </form>
    </Sheet>
  )
}

// --- Delete Category ---

function DeleteCategorySheet({ category, onClose }: { category: Category; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [conflictCount, setConflictCount] = useState<number | null>(null)

  const mutation = useMutation({
    mutationFn: () => deleteCategory(category.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      onClose()
    },
    onError: (err: unknown) => {
      const resp = (err as { response?: { status?: number; data?: { transactionCount?: number } } })
        ?.response
      if (resp?.status === 409 && resp.data?.transactionCount != null) {
        setConflictCount(resp.data.transactionCount)
      }
    },
  })

  if (conflictCount !== null) {
    return (
      <Sheet title="Can't delete this category" onClose={onClose}>
        <p className="mb-6 text-sm text-[var(--fl-text-2)]">
          <strong>{conflictCount}</strong> transaction{conflictCount !== 1 ? 's' : ''} use this
          category. Reassign them before deleting.
        </p>
        <Button variant="outline" onClick={onClose} className="h-11 w-full">
          Close
        </Button>
      </Sheet>
    )
  }

  return (
    <Sheet title="Delete Category" onClose={onClose}>
      <p className="mb-6 text-sm text-[var(--fl-text-2)]">
        Delete <strong>{category.name}</strong>? This cannot be undone.
      </p>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onClose} className="h-11 flex-1">
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="h-11 flex-1"
        >
          {mutation.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </div>
    </Sheet>
  )
}

// --- Rows ---

function CategoryRow({
  category,
  readOnly,
  onEdit,
  onDelete,
}: {
  category: Category
  readOnly: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const { formatWhole } = useFormatCurrency()
  const count = category.transactionCount
  // An API without usage leaves count unknown: offer Delete, and the 409 explains if it's in use.
  const unused = count == null || count === 0
  const usage =
    count == null
      ? null
      : count === 0
        ? 'Not used yet'
        : `${count} transaction${count === 1 ? '' : 's'}${
            category.thisMonthTotal == null
              ? ''
              : category.thisMonthTotal === 0
                ? ' · nothing this month'
                : ` · ${formatWhole(category.thisMonthTotal)} this month`
          }`
  const budget =
    category.type !== 'EXPENSE'
      ? null
      : category.monthlyLimit != null
        ? `${formatWhole(category.monthlyLimit)} / month`
        : category.yearlyLimit != null
          ? `${formatWhole(category.yearlyLimit)} / year`
          : null

  return (
    <li className="flex min-h-[60px] items-center gap-3 py-2.5">
      <CategoryIcon
        icon={category.icon}
        size={15}
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px]',
          category.type === 'INCOME'
            ? 'bg-[var(--fl-primary-subtle)] text-[var(--fl-income)]'
            : 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]'
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate text-sm font-medium text-[var(--fl-text-1)]">
            {category.name}
          </span>
          {category.locked && (
            <Badge>
              <Lock size={10} aria-hidden="true" />
              Used by Loans
            </Badge>
          )}
        </div>
        {usage && <p className="mt-0.5 text-xs text-[var(--fl-text-2)]">{usage}</p>}
      </div>
      {budget && (
        <Badge tone="primary" className="shrink-0 rounded-md tabular-nums">
          {budget}
        </Badge>
      )}
      {!readOnly && (
        <div className="flex shrink-0 items-center">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onEdit}
            aria-label={`Edit ${category.name}`}
            tooltip="Edit"
            className="h-11 w-11 text-[var(--fl-text-2)] sm:h-8 sm:w-8"
          >
            <Pencil size={14} />
          </Button>
          {unused ? (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onDelete}
              aria-label={`Delete ${category.name}`}
              tooltip="Delete"
              className="h-11 w-11 text-[var(--fl-text-2)] hover:text-[var(--fl-error)] sm:h-8 sm:w-8"
            >
              <Trash2 size={14} />
            </Button>
          ) : (
            <span aria-hidden="true" className="w-11 sm:w-8" />
          )}
        </div>
      )}
    </li>
  )
}

// --- Main ---

export function ManageCategoriesTab() {
  const isReadOnly = useIsReadOnly()
  const [search, setSearch] = useState('')
  const [addType, setAddType] = useState<CategoryType | null>(null)
  const [editCategory, setEditCategory] = useState<Category | null>(null)
  const [deleteCategory_, setDeleteCategory] = useState<Category | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['categories', 'usage'],
    queryFn: () => listCategories(undefined, { usage: true }),
  })

  const categories = data?.categories ?? []
  const filtered = categories.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))

  return (
    <div className="flex flex-col gap-4">
      <Input
        type="search"
        aria-label="Search categories"
        placeholder="Search categories…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-11 bg-[var(--fl-surface)] md:max-w-sm"
      />

      {isLoading && (
        <div className="h-40 animate-pulse rounded-2xl bg-[var(--fl-surface-subtle)]" />
      )}
      {isError && <p className="text-sm text-[var(--fl-error)]">Failed to load categories.</p>}

      {!isLoading && !isError && (
        <div className="grid items-start gap-5 lg:grid-cols-2">
          {SECTIONS.map(({ type, title }) => {
            const rows = filtered.filter((c) => c.type === type)
            const total = categories.filter((c) => c.type === type).length
            return (
              <section
                key={type}
                aria-label={`${title} categories`}
                className="rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 pb-1 md:px-5"
              >
                <div className="flex items-center justify-between gap-3 border-b border-[var(--fl-divider)] py-2.5">
                  <div className="flex items-baseline gap-2">
                    <h2
                      className={cn(
                        'text-[15px] font-semibold',
                        type === 'EXPENSE' ? 'text-[var(--fl-expense)]' : 'text-[var(--fl-income)]'
                      )}
                    >
                      {title}
                    </h2>
                    <span className="text-xs text-[var(--fl-text-2)]">
                      {total} categor{total === 1 ? 'y' : 'ies'}
                    </span>
                  </div>
                  {!isReadOnly && (
                    <Button
                      variant="ghost"
                      onClick={() => setAddType(type)}
                      aria-label={`Add ${title.toLowerCase()} category`}
                      className="h-11 gap-1.5 px-2 text-sm font-semibold text-[var(--fl-primary-text)] sm:h-9"
                    >
                      <Plus size={15} /> Add
                    </Button>
                  )}
                </div>
                {rows.length === 0 ? (
                  <p className="py-5 text-center text-sm text-[var(--fl-text-2)]">
                    {search ? 'No matches.' : `No ${title.toLowerCase()} categories yet.`}
                  </p>
                ) : (
                  <ul className="divide-y divide-[var(--fl-divider)]">
                    {rows.map((cat) => (
                      <CategoryRow
                        key={cat.id}
                        category={cat}
                        readOnly={isReadOnly}
                        onEdit={() => setEditCategory(cat)}
                        onDelete={() => setDeleteCategory(cat)}
                      />
                    ))}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      )}

      {addType && <AddCategorySheet initialType={addType} onClose={() => setAddType(null)} />}
      {editCategory && (
        <EditCategorySheet category={editCategory} onClose={() => setEditCategory(null)} />
      )}
      {deleteCategory_ && (
        <DeleteCategorySheet category={deleteCategory_} onClose={() => setDeleteCategory(null)} />
      )}
    </div>
  )
}
